import { Container, Sprite, Text, type Spritesheet } from "pixi.js";
import { ColorOverlayFilter, ExposureFilter, ShineSweepFilter, checkFilterPrograms } from "@slot/presentation";
import type { ViewportSnapshot } from "@slot/presentation";
import type { BootLaunchPayload, SlotApp } from "@slot/game-shell";

/** `?filters=1`: every ported shader on the same sprite, for the e2e screenshot + driver log. */
export function buildFilterTestScene(payload: BootLaunchPayload, slot: SlotApp): { root: Container; layout(snapshot: ViewportSnapshot): void } {
  const sheet = payload.assets.getLoadedAssetOrThrow<Spritesheet>(payload.sharedBundleName, "symbols/atlases/symbols");
  const gl = (slot.app.renderer as unknown as { gl?: WebGL2RenderingContext }).gl;
  if (gl) {
    for (const check of checkFilterPrograms(gl)) {
      console.log(`[filters] ${check.name}: ${check.ok ? "ok" : "FAILED"}${check.log ? ` — ${check.log}` : ""}`);
    }
  }
  const root = new Container();
  const texture = sheet.textures["H4"] ?? Object.values(sheet.textures)[0]!;
  const variants: Array<{ label: string; filter: ExposureFilter | ShineSweepFilter | ColorOverlayFilter | null }> = [
    { label: "none", filter: null },
    { label: "exposure +1", filter: new ExposureFilter(1) },
    { label: "exposure -0.7", filter: new ExposureFilter(-0.7) },
    { label: "shine 0.5", filter: new ShineSweepFilter({ progress: 0.5 }) },
    { label: "overlay darken", filter: new ColorOverlayFilter({ mode: "darken", strength: 1 }) },
    { label: "overlay soft", filter: new ColorOverlayFilter({ mode: "softLight", strength: 1, tintStrength: 0.5 }) },
  ];
  const items = variants.map((v) => {
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    if (v.filter) sprite.filters = [v.filter];
    const caption = new Text({ text: v.label, style: { fill: 0xffffff, fontSize: 20, fontFamily: "Roboto, system-ui, sans-serif" } });
    caption.anchor.set(0.5, 0);
    root.addChild(sprite, caption);
    return { sprite, caption };
  });
  const layout = (snapshot: ViewportSnapshot): void => {
    const { width, height } = snapshot.design;
    const columns = width > height ? 3 : 2;
    const cell = Math.min(width / columns, height / Math.ceil(items.length / columns)) * 0.9;
    items.forEach(({ sprite, caption }, i) => {
      const column = i % columns;
      const row = Math.floor(i / columns);
      const x = (column - (columns - 1) / 2) * cell;
      const y = (row - (Math.ceil(items.length / columns) - 1) / 2) * cell;
      sprite.position.set(x, y - 12);
      sprite.scale.set((cell * 0.6) / Math.max(texture.width, texture.height));
      caption.position.set(x, y + cell * 0.32);
    });
  };
  return { root, layout };
}
